import React, { useEffect, useState, useRef } from "react";
import api from "@/api";
import { Play, Copy, Check, Upload, Link2, Code2, Loader2, AlertCircle, ChevronDown, ChevronUp, Zap, CheckCircle, XCircle } from "lucide-react";
import { copyToClipboard } from "@/utils/clipboard";

const MIME_TYPES = [
  { label: "PDF", value: "application/pdf" },
  { label: "JPEG", value: "image/jpeg" },
  { label: "PNG", value: "image/png" },
  { label: "WebP", value: "image/webp" },
];

function CodeBlock({ code, language = "json" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { copyToClipboard(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative rounded-md border border-slate-200 bg-slate-950 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-900 border-b border-slate-800">
        <span className="text-xs font-mono text-slate-400">{language}</span>
        <button onClick={copy} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white transition-colors">
          {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
        </button>
      </div>
      <pre className="p-4 text-xs font-mono text-slate-200 overflow-x-auto max-h-72 overflow-y-auto whitespace-pre-wrap break-words">{code}</pre>
    </div>
  );
}

/* ── Shared file input panel ─────────────────────────────────────────────── */
function FileInputPanel({ mode, setMode, file, setFile, mimeType, setMimeType, base64Input, setBase64Input, urlInput, setUrlInput, fileRef }) {
  const modeBtn = (m, Icon, label) => (
    <button type="button" onClick={() => setMode(m)} data-testid={`playground-mode-${m}`}
      className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded border transition-colors ${mode === m ? "bg-[#006080] text-white border-[#006080]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006080]/50"}`}>
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-2">Input Mode</label>
        <div className="flex gap-2 flex-wrap">
          {modeBtn("file", Upload, "File")}
          {modeBtn("base64", Code2, "Base64")}
          {modeBtn("url", Link2, "URL")}
        </div>
      </div>
      {mode === "file" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Document</label>
          <div onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-[#006080]/40 hover:bg-slate-50 transition-colors">
            {file ? <p className="text-sm text-slate-700 font-medium truncate">{file.name}</p> : (
              <><Upload size={20} className="text-slate-300 mx-auto mb-1" /><p className="text-xs text-slate-400">Click to select PDF or image</p></>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="playground-file-input"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) { setFile(f); const d = MIME_TYPES.find(m => m.value === f.type); if (d) setMimeType(d.value); }
              }} />
          </div>
        </div>
      )}
      {mode === "base64" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Base64 String</label>
          <textarea data-testid="playground-base64-input" value={base64Input} onChange={e => setBase64Input(e.target.value)}
            placeholder="Paste base64-encoded document..." rows={4}
            className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none" />
        </div>
      )}
      {mode === "url" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Document URL</label>
          <input data-testid="playground-url-input" value={urlInput} onChange={e => setUrlInput(e.target.value)}
            placeholder="https://example.com/document.pdf"
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
        </div>
      )}
      {(mode === "base64" || mode === "url") && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">MIME Type</label>
          <select data-testid="playground-mime-select" value={mimeType} onChange={e => setMimeType(e.target.value)}
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
            {MIME_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

/* ── Schema extraction result display ────────────────────────────────────── */
function SchemaResult({ result }) {
  if (!result) return null;
  return (
    <div className="space-y-4" data-testid="playground-result">
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="p-3 bg-slate-50 rounded-md border border-slate-100">
          <p className="text-slate-500 mb-0.5">Pages Processed</p>
          <p className="font-bold text-slate-900 text-lg">{result.pages_processed || 0}</p>
        </div>
        <div className="p-3 bg-slate-50 rounded-md border border-slate-100">
          <p className="text-slate-500 mb-0.5">Document ID</p>
          <p className="font-mono text-[10px] text-slate-700 truncate">{result.document_id}</p>
        </div>
      </div>
      {result.extracted_fields && Object.keys(result.extracted_fields).length > 0 ? (
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Extracted Fields</p>
          <div className="space-y-1.5">
            {Object.entries(result.extracted_fields).map(([key, value]) => (
              <div key={key} className="flex items-start gap-3 p-2.5 bg-[#E5F0F2]/50 rounded border border-[#006080]/10">
                <span className="text-xs font-mono text-[#006080] font-semibold flex-shrink-0 min-w-24">{key}</span>
                <span className="text-xs text-slate-700 break-all">{value === null ? <span className="text-slate-400 italic">null</span> : String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">No fields extracted.</p>
      )}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Full JSON Response</p>
        <CodeBlock code={JSON.stringify(result, null, 2)} language="json" />
      </div>
    </div>
  );
}

/* ── Pipeline result display ─────────────────────────────────────────────── */
function PipelineResult({ result }) {
  if (!result) return null;
  const matched = result.matched_templates || [];
  const other = result.other_templates || [];
  return (
    <div className="space-y-4" data-testid="pipeline-result">
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div className="p-3 bg-slate-50 rounded-md border border-slate-100">
          <p className="text-slate-500 mb-0.5">Pages</p>
          <p className="font-bold text-slate-900 text-lg">{result.pages_processed || 0}</p>
        </div>
        <div className="p-3 bg-emerald-50 rounded-md border border-emerald-100">
          <p className="text-emerald-600 mb-0.5">Matched</p>
          <p className="font-bold text-emerald-700 text-lg">{matched.length}</p>
        </div>
        <div className="p-3 bg-slate-50 rounded-md border border-slate-100">
          <p className="text-slate-500 mb-0.5">Other</p>
          <p className="font-bold text-slate-900 text-lg">{other.length}</p>
        </div>
      </div>

      {matched.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2 flex items-center gap-1.5">
            <CheckCircle size={12} /> Matched Templates
          </p>
          <div className="space-y-3">
            {matched.map(t => (
              <div key={t.schema_id} className="border border-emerald-200 bg-emerald-50/30 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50">
                  <CheckCircle size={12} className="text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-slate-800">{t.schema_name}</span>
                  <span className="ml-auto text-[10px] text-emerald-600 bg-white px-2 py-0.5 rounded border border-emerald-200">{t.match_reason}</span>
                </div>
                {Object.keys(t.extracted_fields || {}).length > 0 && (
                  <div className="px-3 py-2 space-y-1">
                    {Object.entries(t.extracted_fields).map(([k, v]) => (
                      <div key={k} className="flex items-start gap-3">
                        <span className="text-[11px] font-mono text-[#006080] font-semibold flex-shrink-0 min-w-20">{k}</span>
                        <span className="text-[11px] text-slate-700 break-all">{v === null ? <span className="italic text-slate-400">null</span> : String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {other.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
            <XCircle size={12} /> Other Templates
          </p>
          <div className="space-y-2">
            {other.map(t => (
              <div key={t.schema_id} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg bg-slate-50">
                <XCircle size={12} className="text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-600">{t.schema_name}</span>
                <span className="ml-auto text-[10px] text-slate-400">{t.match_reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Full JSON Response</p>
        <CodeBlock code={JSON.stringify(result, null, 2)} language="json" />
      </div>
    </div>
  );
}

/* ── Main playground ─────────────────────────────────────────────────────── */
export default function ApiPlayground() {
  const [tab, setTab] = useState("schema"); // "schema" | "pipeline"
  const [schemas, setSchemas] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [mode, setMode] = useState("file");
  const [selectedSchema, setSelectedSchema] = useState("");
  const [selectedApiKey, setSelectedApiKey] = useState("");
  const [selectedPipeline, setSelectedPipeline] = useState("");
  const [file, setFile] = useState(null);
  const [base64Input, setBase64Input] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [mimeType, setMimeType] = useState("application/pdf");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [showCurl, setShowCurl] = useState(true);
  const fileRef = useRef();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/schemas").then(r => setSchemas(r.data)).catch(() => {});
    api.get("/api-keys").then(r => {
      const active = r.data.filter(k => k.is_active);
      setApiKeys(active);
      if (active.length > 0) setSelectedApiKey(active[0].id);
    }).catch(() => {});
    api.get("/pipelines").then(r => {
      const active = (r.data || []).filter(p => p.is_active);
      setPipelines(active);
      if (active.length > 0) setSelectedPipeline(active[0].id);
    }).catch(() => {});
  }, []);

  const buildCurl = () => {
    if (tab === "pipeline") {
      const pipeline = pipelines.find(p => p.id === selectedPipeline);
      const body = {
        filename: mode === "url" ? (urlInput.split("/").pop() || "document.pdf") : (file?.name || "document.pdf"),
        mime_type: mimeType,
        ...(mode === "base64" ? { file_base64: "<BASE64_STRING>" } : {}),
        ...(mode === "url" ? { file_url: urlInput || "<FILE_URL>" } : {}),
        ...(mode === "file" ? { file_base64: "<BASE64_FROM_FILE>" } : {}),
      };
      return `curl -X POST "${BACKEND_URL}/api/pipelines/${selectedPipeline || "<PIPELINE_ID>"}/ingest" \\
  -H "Content-Type: application/json" \\
  -H "X-Pipeline-Key: <YOUR_PIPELINE_KEY>" \\
  -d '${JSON.stringify(body, null, 2)}'`;
    }
    const body = {
      filename: mode === "url" ? (urlInput.split("/").pop() || "document.pdf") : (file?.name || "document.pdf"),
      mime_type: mimeType,
      schema_id: selectedSchema || undefined,
      ...(mode === "base64" ? { file_base64: "<BASE64_STRING>" } : {}),
      ...(mode === "url" ? { file_url: urlInput || "<FILE_URL>" } : {}),
      ...(mode === "file" ? { file_base64: "<BASE64_FROM_FILE>" } : {}),
    };
    return `curl -X POST "${BACKEND_URL}/api/v1/ingest" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: <YOUR_API_KEY>" \\
  -d '${JSON.stringify(body, null, 2)}'`;
  };

  const handleRun = async () => {
    setError(""); setResult(null); setRunning(true);
    try {
      let fileBase64 = null;
      let filename = "document.pdf";
      if (mode === "file") {
        if (!file) { setError("Please select a file."); setRunning(false); return; }
        filename = file.name;
        fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      } else if (mode === "base64") {
        if (!base64Input.trim()) { setError("Please enter a base64 string."); setRunning(false); return; }
        fileBase64 = base64Input.trim();
      } else if (mode === "url") {
        if (!urlInput.trim()) { setError("Please enter a file URL."); setRunning(false); return; }
      }

      if (tab === "pipeline") {
        if (!selectedPipeline) { setError("Please select a pipeline."); setRunning(false); return; }
        const payload = {
          pipeline_id: selectedPipeline,
          filename: filename || "document.pdf",
          mime_type: mimeType,
          ...(mode === "url" ? { file_url: urlInput } : { file_base64: fileBase64 }),
        };
        const r = await api.post("/playground/pipeline-extract", payload);
        setResult(r.data);
      } else {
        const keyDoc = apiKeys.find(k => k.id === selectedApiKey);
        if (!keyDoc) { setError("Please select an API key."); setRunning(false); return; }
        const payload = {
          filename: filename || "document.pdf",
          mime_type: mimeType,
          schema_id: selectedSchema || undefined,
          ...(mode === "url" ? { file_url: urlInput } : { file_base64: fileBase64 }),
        };
        const r = await api.post("/playground/extract", payload);
        setResult(r.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Extraction failed.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="playground-page">
      <div>
        <h2 className="text-xl font-bold text-slate-900">API Playground</h2>
        <p className="text-sm text-slate-500 mt-0.5">Test document extraction interactively — no code required.</p>
      </div>

      {/* Tab selector */}
      <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 w-fit">
        <button data-testid="playground-tab-schema" onClick={() => { setTab("schema"); setResult(null); setError(""); }}
          className={`flex items-center gap-1.5 h-8 px-4 text-sm font-medium rounded-md transition-colors ${tab === "schema" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <Code2 size={13} /> Schema Extraction
        </button>
        <button data-testid="playground-tab-pipeline" onClick={() => { setTab("pipeline"); setResult(null); setError(""); }}
          className={`flex items-center gap-1.5 h-8 px-4 text-sm font-medium rounded-md transition-colors ${tab === "pipeline" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <Zap size={13} /> Pipeline Test
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Input panel */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Input</h3>

            <FileInputPanel mode={mode} setMode={setMode} file={file} setFile={setFile}
              mimeType={mimeType} setMimeType={setMimeType} base64Input={base64Input}
              setBase64Input={setBase64Input} urlInput={urlInput} setUrlInput={setUrlInput} fileRef={fileRef} />

            {/* Schema-specific controls */}
            {tab === "schema" && (
              <>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Schema (optional)</label>
                  <select data-testid="playground-schema-select" value={selectedSchema} onChange={e => setSelectedSchema(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
                    <option value="">Auto-detect / No schema</option>
                    {schemas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">API Key</label>
                  {apiKeys.length === 0 ? (
                    <p className="text-xs text-slate-400">No API keys — <a href="/api-keys" className="text-[#006080] hover:underline">create one</a>.</p>
                  ) : (
                    <select data-testid="playground-apikey-select" value={selectedApiKey} onChange={e => setSelectedApiKey(e.target.value)}
                      className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
                      {apiKeys.map(k => <option key={k.id} value={k.id}>{k.name || k.key_prefix + "..."}</option>)}
                    </select>
                  )}
                </div>
              </>
            )}

            {/* Pipeline-specific controls */}
            {tab === "pipeline" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Pipeline <span className="text-red-400">*</span></label>
                {pipelines.length === 0 ? (
                  <p className="text-xs text-slate-400">No active pipelines — <a href="/pipelines/new" className="text-[#006080] hover:underline">create one</a>.</p>
                ) : (
                  <select data-testid="playground-pipeline-select" value={selectedPipeline} onChange={e => setSelectedPipeline(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
                    {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
                <p className="text-xs text-slate-400 mt-1">Playground bypasses the pipeline key requirement for quick testing.</p>
              </div>
            )}

            <button onClick={handleRun} data-testid="playground-run-button" disabled={running}
              className="w-full h-10 bg-[#006080] text-white text-sm font-semibold rounded-md hover:bg-[#004B66] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
              {running ? <><Loader2 size={15} className="animate-spin" /> Processing…</> : <><Play size={15} /> {tab === "pipeline" ? "Run Pipeline" : "Run Extraction"}</>}
            </button>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}
          </div>
        </div>

        {/* Right: cURL + Response */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <button type="button" onClick={() => setShowCurl(c => !c)}
              className="w-full flex items-center justify-between px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate-500 hover:bg-slate-50 transition-colors">
              <span>cURL Equivalent</span>
              {showCurl ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showCurl && (
              <div className="px-5 pb-4">
                <CodeBlock code={buildCurl()} language="bash" />
                {tab === "pipeline" && (
                  <p className="text-xs text-slate-400 mt-2">Replace <code className="bg-slate-100 px-1 rounded font-mono">&lt;YOUR_PIPELINE_KEY&gt;</code> with a key from the Pipeline builder.</p>
                )}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Response</h3>
            {!result && !running && (
              <div className="py-10 text-center">
                <Play size={24} className="text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Click "{tab === "pipeline" ? "Run Pipeline" : "Run Extraction"}" to see results</p>
              </div>
            )}
            {running && (
              <div className="py-10 text-center">
                <Loader2 size={24} className="text-[#006080] mx-auto mb-2 animate-spin" />
                <p className="text-sm text-slate-500">Processing document…</p>
              </div>
            )}
            {result && tab === "schema" && <SchemaResult result={result} />}
            {result && tab === "pipeline" && <PipelineResult result={result} />}
          </div>
        </div>
      </div>
    </div>
  );
}
